<script context="module">
  export const ssr = true
  export const prerender = true
</script>

<script>
  import { PixelatedContainer } from '$lib/components/containers'

  const clear = (_) => {
    if (
      !confirm(
        'This will delete all of your saved data and could degrade app performance. Are you sure you want to continue?'
      )
    )
      return

    Object.keys(window.localStorage)
      .filter((i) => i.startsWith('nuzlocke'))
      .forEach((it) => window.localStorage.removeItem(it))

    navigator.serviceWorker.getRegistrations().then(function (registrations) {
      for (let registration of registrations) {
        registration.unregister()
      }
    })

    alert('All data deleted successfully')
  }
</script>

<svelte:head>
  <title>Nuzlocke Tracker | Privacy Policy</title>
  <meta
    name="description"
    content="Privacy policy for the Pokemon Nuzlocke Tracker, including local save data, Google Analytics, Google AdSense, cookies, and contact information."
  />
  <meta
    property="og:description"
    content="Privacy policy for the Pokemon Nuzlocke Tracker, including local save data, Google Analytics, Google AdSense, cookies, and contact information."
  />
</svelte:head>

<main class="container mx-auto px-2 pb-24 pt-8 text-gray-800 dark:text-gray-300 sm:pt-0">
  <div class="mx-4 mx-auto pb-16 sm:pb-12">
    <PixelatedContainer className="my-16 py-6 px-8 overflow-x-hidden mx-auto max-w-prose">
      <h1 class="text-2xl">Privacy Policy</h1>
      <p class="meta">Last updated: June 6th, 2026</p>

      <p>
        Nuzlocke Tracker is a browser-based tool for planning and tracking Pokemon Nuzlocke
        runs. You can use the site without creating an account or providing personal
        information.
      </p>

      <h2>Local Save Data</h2>
      <p>
        Your run data is stored in your own browser through
        <a href="https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage">
          <strong>Window.localStorage</strong>
        </a>. This keeps your save files on your device so you can return to them later.
      </p>

      <table class="my-6 table-fixed overflow-x-scroll">
        <thead class="border-b border-white">
          <tr>
            <th>Key</th>
            <th>Purpose</th>
            <th>Example</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>nuzlocke.consent</code></td>
            <td>Whether you have previously consented to local data storage</td>
            <td><code>true</code></td>
          </tr>
          <tr>
            <td><code>nuzlocke</code></td>
            <td>The identifier for the current save file</td>
            <td><code>ccf2288e-fb30-4a80-960e-ef1be4f42018</code></td>
          </tr>
          <tr>
            <td><code>nuzlocke.theme</code></td>
            <td>Your dark or light theme preference</td>
            <td><code>dark</code></td>
          </tr>
          <tr>
            <td><code>nuzlocke.saves</code></td>
            <td>A list of saved games</td>
            <td><code>id|timestamp|name|gameId</code></td>
          </tr>
          <tr>
            <td><code>nuzlocke.[GAMEID]</code></td>
            <td>A JSON representation of your Pokemon and starter type</td>
            <td><code>{JSON.stringify({ __starter: 'fire', 'Route 1': { id: 0, pokemon: 'charmander', status: 1 } })}</code></td>
          </tr>
        </tbody>
      </table>

      <p>
        You can clear your stored Nuzlocke Tracker data by
        <button
          on:click={clear}
          class="underline hover:cursor-pointer"
          title="Clear all stored data"
        >
          clicking here
        </button>.
      </p>

      <h2>Analytics And Advertising</h2>
      <p>
        We use Google Analytics to understand site traffic and improve the tracker. We also use
        Google AdSense to display ads. Google and its advertising partners may use cookies or
        similar technologies to serve ads, limit how often ads appear, measure ad performance, and
        personalize ads depending on your settings.
      </p>
      <p>
        You can review and change your Google ad personalization settings at
        <a href="https://myadcenter.google.com/">Google My Ad Center</a>. You can also learn more
        about how Google uses information from sites and apps at
        <a href="https://policies.google.com/technologies/partner-sites">
          Google's partner sites policy
        </a>.
      </p>

      <h2>Third-Party Services</h2>
      <p>
        The site may load assets or services from third parties, including Google Analytics, Google
        AdSense, Google Fonts, image/CDN providers, and error monitoring tools. These services may
        process technical information such as browser type, IP address, device information, pages
        visited, and timestamps according to their own privacy policies.
      </p>

      <h2>Contact Information</h2>
      <p>
        If you contact us by email, we will use your email address and message only to respond to
        your request, investigate bugs, or handle privacy questions.
      </p>

      <h2>Changes To This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Updates take effect when posted on
        this page.
      </p>

      <h2>Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy, contact us at
        <a href="mailto:nuzlocke.tracker@gmail.com">nuzlocke.tracker@gmail.com</a>.
      </p>
    </PixelatedContainer>
  </div>
</main>

<style lang="postcss">
  td + td + td,
  th + th + th {
    display: none;
  }

  @media (min-width: 640px) {
    td + td + td,
    th + th + th {
      display: block;
    }
  }

  h2 {
    @apply mb-2 mt-6 text-xl;
  }

  p {
    margin-bottom: 16px;
  }

  tr,
  thead {
    font-size: 0.75rem;
  }

  thead {
    text-align: left;
  }

  tr code {
    font-size: 1rem;
  }

  a {
    text-decoration: underline;
    transition: all 0.3s ease;
  }

  a:hover {
    color: white;
  }

  .meta {
    @apply -mt-px text-xs;
  }
</style>
